// Jam (/jam): the live jams you can listen in on or join, and
// the way to start one.
//
// The only multiplayer surface — the desktop nav and the phone's tab bar both
// come here, and the home page links it by its live count. A jam always grows
// out of a seed, so "Pick a seed" goes to the jam picker (/jam/start, the feed
// with Start Jam on every card) rather than pretending there is a blank room
// to open. A seed page's own "Start a jam" does the same from there.

import { useState } from "react";
import { Link } from "react-router";
import { Check, Copy, Disc3, ExternalLink, Users } from "lucide-react";
import { Button } from "@web-shared/components/ui/button";
import { roomPath } from "@/lib/ctas";
import { relativeTime } from "@web-shared/lib/format";
import { participantKey } from "@shared/api-types.js";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useLiveJams, type LiveJam } from "../live";
import { Avatar } from "../components/Avatar";
import { AmbientGlow } from "@/components/AmbientGlow";


const DISCORD = "https://seed-bed.com/join";

export function JamPage() {
  const live = useLiveJams();
  useDocumentTitle("Jam");

  return (
    <div className="space-y-6">
      <AmbientGlow />

      {/* Mobile header */}
      <header className="sm:hidden">
        <h1 className="text-4xl font-black tracking-tighter">Jam</h1>
        <p className="text-muted-foreground mt-2 text-sm">
          Make music with other producers, live. Everyone edits the same session.
        </p>
      </header>
      {/* Desktop header */}
      <header className="hidden sm:flex flex-wrap items-baseline justify-between gap-3">
        <h1 className="text-3xl font-black tracking-tight">Jam</h1>
        {live.length > 0 && (
          <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">
            <span className="size-1.5 rounded-full bg-primary animate-pulse" />
            {live.length} live now
          </span>
        )}
      </header>

      {/* ---- Mobile layout: stacked ---- */}
      <div className="sm:hidden space-y-6">
        <section className="flex flex-col gap-3" aria-label="Live jams">
          <div className="flex items-center gap-2 text-[11px] font-bold tracking-widest uppercase text-muted-foreground">
            {live.length > 0 && <span className="size-1.5 rounded-full bg-primary animate-pulse" />}
            Live now{live.length > 0 ? ` · ${live.length}` : ""}
          </div>
          {live.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-strong p-8 text-center text-sm text-muted-foreground">
              No one is jamming right now. Start one and be the first.
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {live.map((jam) => <JamCard key={jam.id} jam={jam} />)}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-dashed border-primary/30 bg-primary/[0.04] p-4">
          <div className="text-lg font-black">Start a jam</div>
          <p className="text-sm text-muted-foreground mt-1">
            Pick a seed, share the link, and whoever joins plays along live.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <Button asChild className="rounded-xl font-bold h-11">
              <Link to="/jam/start">Pick a seed</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-xl font-semibold h-11">
              <Link to="/charts">Use the Weekly Flip</Link>
            </Button>
          </div>
        </section>

        <div className="text-center">
          <a href={DISCORD} target="_blank" rel="noopener" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
            Find people to jam with on Discord →
          </a>
        </div>
      </div>

      {/* ---- Desktop layout: 3-col grid ---- */}
      <div className="hidden sm:grid gap-6 lg:grid-cols-3">
        <section className="flex flex-col gap-3 lg:col-span-2" aria-label="Live jams">
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground">Live now</h2>
          {live.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-strong p-8 text-center text-sm text-muted-foreground">
              No one is jamming right now. Start one and be the first.
            </div>
          ) : (
            live.map((jam) => <JamCard key={jam.id} jam={jam} />)
          )}
        </section>

        <StartAJam />
      </div>
    </div>
  );
}

/** Desktop sidebar: pick a seed, open it in the browser, invite people in. */
function StartAJam() {
  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-dashed border-primary/30 bg-primary/[0.04] p-6">
      <h2 className="text-2xl font-black tracking-tight">Start a jam</h2>
      <p className="text-sm leading-relaxed text-muted-foreground">
        Pick a seed and start a jam on it — that opens a live room. Send the link to
        whoever you want in: everyone edits the same session live, in here and in Live.
      </p>
      <Button asChild className="mt-auto rounded-full py-5 font-bold">
        <Link to="/jam/start">Pick a seed</Link>
      </Button>
      <a
        href={DISCORD}
        target="_blank"
        rel="noopener"
        className="text-center text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        Find people on Discord <ExternalLink className="ml-0.5 inline size-3.5 align-[-2px]" />
      </a>
    </section>
  );
}

function JamCard({ jam }: { jam: LiveJam }) {
  return (
    <article className="rounded-2xl bg-raised border border-hairline p-3.5 sm:p-5 transition-colors hover:border-strong">
      <div className="flex flex-wrap items-center gap-3 sm:gap-4">
        <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Disc3 className="size-6 animate-spin text-primary" style={{ animationDuration: "3s" }} />
        </div>

        <div className="min-w-0 flex-1">
          <p className="truncate font-bold">{jam.title}</p>
          {/* Mobile: compact info */}
          <div className="text-xs text-muted-foreground truncate sm:hidden">
            {jam.participants.map((p) => p.username).join(", ")} · started {relativeTime(jam.createdAt)}
          </div>
          {/* Desktop: info with avatars */}
          <div className="mt-1 hidden sm:flex items-center gap-2">
            <Users className="size-3.5 text-muted-foreground" aria-hidden />
            <div className="flex -space-x-1.5">
              {jam.participants.map((p) => (
                <Avatar key={participantKey(p)} name={p.username} url={null} className="size-5 ring-2 ring-raised" />
              ))}
            </div>
            <span className="text-xs text-muted-foreground">
              {jam.participants.length} {jam.participants.length === 1 ? "person" : "people"} · started {relativeTime(jam.createdAt)}
            </span>
          </div>
        </div>

        {/* Mobile: single Join button */}
        <Link
          to={roomPath(jam.id)}
          className="h-10 px-4 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center sm:hidden"
        >
          Join
        </Link>
        {/* Desktop: full button set */}
        <div className="hidden sm:flex shrink-0 gap-2">
          <InviteLink jamId={jam.id} />
          <Button variant="outline" size="sm" asChild>
            <Link to={`/jams/${encodeURIComponent(jam.id)}`}>Listen in</Link>
          </Button>
          <Button size="sm" asChild>
            <Link to={roomPath(jam.id)}>Join</Link>
          </Button>
        </div>
      </div>

      {/* Mobile-only: participants row */}
      <div className="mt-3 flex items-center justify-between sm:hidden">
        <div className="flex -space-x-2">
          {jam.participants.map((p) => (
            <Avatar key={participantKey(p)} name={p.username} url={null} className="size-7 border-2 border-raised" />
          ))}
        </div>
        <span className="text-[11px] text-muted-foreground/60">
          {jam.participants.length} in the room
        </span>
      </div>
    </article>
  );
}

function InviteLink({ jamId }: { jamId: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const url = `${window.location.origin}${roomPath(jamId)}`;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={copy}
      title="Copy invite link"
      aria-label={copied ? "Invite link copied" : "Copy invite link"}
      className="size-8"
    >
      {copied ? <Check className="size-3.5 text-primary" /> : <Copy className="size-3.5" />}
    </Button>
  );
}
